import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import AntCheckBox from "../../../FormBuilderInputs/CheckBox";
import AntDatePicker from "../../../FormBuilderInputs/DatePicker";
import DropdownInput from "../../../FormBuilderInputs/DropdownInput";
import NormalInput from "../../../FormBuilderInputs/NormalInput";
import ContainerPreview from "../../../FormBuilderInputs/ContainerPreview";
import RadioGroup from "../../../FormBuilderInputs/RadioGroup";
import AntTextArea from "../../../FormBuilderInputs/TextArea";
import Toggle from "../../../FormBuilderInputs/Toggle";
import AntUpload from "../../../FormBuilderInputs/Upload";
import AntHeader from "../../../FormBuilderInputs/Header";
import Image from "../../../FormBuilderInputs/Image";
import Label from "../../../FormBuilderInputs/Label";
import Link from "../../../FormBuilderInputs/Link";
import UISignature from "../../../FormBuilderInputs/Signature";
import SnippetCamera from "../../../FormBuilderInputs/Camera";
import Popup from "../../../Helpers/Popup/Popup";
import PopupTitle from "../../../Helpers/Popup/PopupParts/PopupTitle";
import Close from "../../../Helpers/CloseBtn/Close";
import Button from "../../../Helpers/Button/Button";
import Loader from "../../../Helpers/Loader/Loader";
import trans from "../../../../utils";
import {
	useAddNearMissMutation,
	useGetSpecificFormQuery,
} from "../../../../Redux/service/Forms/Form";

const NEAR_MISS_FORM_ID = Number(process.env.REACT_APP_NEAR_MISS_FORM_ID);

// Schema entries that only decorate the form - they never carry a submitted value.
const DECORATIVE_FIELDS = [
	"Header",
	"Image",
	"Label",
	"Link",
	"Button",
	"Progress_circle",
	"Progress_line",
];

// Walk into Container fields so nested inputs are validated too.
const flattenFields = (fields) =>
	(fields || []).flatMap((field) =>
		field?.name === "Container" && Array.isArray(field.container)
			? [field, ...flattenFields(field.container)]
			: [field]
	);

const isEmpty = (value) =>
	value === undefined ||
	value === null ||
	value === "" ||
	(Array.isArray(value) && value.length === 0);

// Signature / Camera hand back a raw data URL. The backend turns a
// { base64, filename } value into a stored file plus an attachment row, which is what the
// printed report reads the signature from - a bare data URL would be kept as text instead.
const toPayloadValue = (field) => {
	if (
		typeof field.value === "string" &&
		field.value.startsWith("data:") &&
		["Signature", "Camera", "takephoto", "Uploader"].includes(field.name)
	) {
		const extension = field.value.match(/^data:image\/([a-zA-Z]+);/)?.[1] || "png";
		return {
			base64: field.value,
			filename: `${field.name.toLowerCase()}_${Date.now()}.${extension}`,
		};
	}
	return field.value;
};

export default function NearMissAddPopup({ show, setShow, onSuccess }) {
	const [submitData, setSubmitData] = useState([]);
	// Remounts the inputs on every open so a cancelled report doesn't leak into the next one.
	const [formInstance, setFormInstance] = useState(0);

	const { data: formDefinition, isLoading: isFormLoading } =
		useGetSpecificFormQuery(NEAR_MISS_FORM_ID, { skip: !show });

	const [addNearMiss, { isLoading: isSaving }] = useAddNearMissMutation();

	const schemaFields = formDefinition?.data?.formSchema?.fields || [];

	const addForm = useFormik({
		initialValues: { fields: [] },
		onSubmit: async (values) => {
			try {
				await addNearMiss({ fields: values.fields }).unwrap();
				toast.success(trans("Near Miss Report Submitted Successfully"), {
					autoClose: 800,
					rtl: localStorage.getItem("lang") === "ar",
				});
				setSubmitData([]);
				setShow(false);
				if (onSuccess) onSuccess();
			} catch (error) {
				toast.error(`${error?.data?.message || trans("Something went wrong")}`, {
					autoClose: 1500,
					rtl: localStorage.getItem("lang") === "ar",
				});
			}
		},
	});

	// Opening the popup starts a clean report: values, the "touched" flags that drive the
	// red required markers, and the inputs' own internal state all go back to empty.
	useEffect(() => {
		if (show) {
			setSubmitData([]);
			addForm.resetForm();
			setFormInstance((prev) => prev + 1);
		}
		// addForm is a new object every render - depending on it would loop.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show]);

	const handleSubmit = (e) => {
		e.preventDefault();

		const allFields = flattenFields(schemaFields).filter(
			(field) => field?.key && !DECORATIVE_FIELDS.includes(field.name)
		);

		const missingRequired = allFields.filter((field) => {
			const submitted = submitData.find((i) => i.key === field.key);
			return field?.required?.value && isEmpty(submitted?.value);
		});

		if (missingRequired.length > 0) {
			missingRequired.forEach((field) => {
				addForm.setFieldTouched(`fields[${field.key}].value`, true, false);
			});
			toast.error(
				trans(
					missingRequired[0]?.required?.validationMessage ||
						"Please fill in the required fields"
				),
				{ autoClose: 1500, rtl: localStorage.getItem("lang") === "ar" }
			);
			return;
		}

		addForm.values.fields = submitData.map((field) => ({
			...field,
			value: toPayloadValue(field),
		}));
		addForm.handleSubmit(e);
	};

	const getInputType = (item) => {
		const shared = {
			item,
			addData: addForm,
			submitData,
			setSubmitData,
		};

		switch (item?.name) {
			case "Checkbox":
				return <AntCheckBox {...shared} readOnly={false} />;
			case "DatePicker":
				// The printed report has a Time cell next to the Date cell, both read off this
				// one value, so the picker has to capture the time as well.
				return (
					<AntDatePicker
						{...shared}
						preview={true}
						disable={true}
						showTime={true}
					/>
				);
			case "Dropdown":
				return <DropdownInput {...shared} preview={true} readOnly={false} />;
			case "Input":
				return <NormalInput {...shared} preview={true} readOnly={false} />;
			case "Container":
				return (
					<ContainerPreview
						getInputType={getInputType}
						ukey={item?.id}
						item={item?.container}
						css={item?.css}
					/>
				);
			case "Radio_group":
				return <RadioGroup {...shared} preview={true} readOnly={false} />;
			case "Text_area":
				return <AntTextArea {...shared} preview={true} readOnly={false} />;
			case "Toggle":
				return <Toggle {...shared} readOnly={false} />;
			case "Uploader":
				return <AntUpload {...shared} readOnly={false} />;
			case "Signature":
				return <UISignature {...shared} preview={true} readOnly={false} />;
			case "Camera":
				return <SnippetCamera {...shared} preview={true} />;
			case "Header":
				return <AntHeader item={item} />;
			case "Image":
				return <Image item={item} />;
			case "Label":
				return <Label item={item} />;
			case "Link":
				return <Link item={item} />;
			default:
				return null;
		}
	};

	return (
		<Popup show={show}>
			<Modal.Header className="modal-header flex-between">
				<Modal.Title id="contained-modal-title-vcenter">
					<PopupTitle
						headtitle={formDefinition?.data?.name || trans("Near Miss Report")}
						subtitle={trans("Report a near miss")}
					/>
				</Modal.Title>
				<Close
					// eslint-disable-next-line react/style-prop-object
					style="close-32 close"
					close={() => setShow(false)}
					img="Close"
				/>
			</Modal.Header>
			{/* The body itself does not scroll - it is a flex column holding a scrollable field
			    area above an action bar that stays put, so Cancel/Submit are always reachable
			    on a form this long. */}
			<Modal.Body
				style={{
					height: "calc(100vh - 200px)",
					maxHeight: "calc(100vh - 200px)",
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
					padding: 0,
				}}
			>
				{isFormLoading ? (
					<div className="loader-container p-16">
						<Loader />
					</div>
				) : (
					<form
						onSubmit={handleSubmit}
						key={formInstance}
						style={{
							display: "flex",
							flexDirection: "column",
							flex: 1,
							minHeight: 0,
						}}
					>
						<div
							className="modal-body-scrollable"
							style={{
								flex: 1,
								minHeight: 0,
								maxHeight: "none",
								overflowY: "auto",
								overflowX: "hidden",
								padding: "16px",
							}}
						>
							{schemaFields.map((item) => (
								<div className="mb-2" style={item.css} key={item.key}>
									{getInputType(item)}
								</div>
							))}
						</div>
						<div
							className="d-flex gap-2 justify-content-end"
							style={{
								flexShrink: 0,
								padding: "12px 16px",
								borderTop: "1px solid #eaecf0",
								backgroundColor: "#fff",
							}}
						>
							<Button
								text={trans("Cancel")}
								type="default"
								htmlType="button"
								disabled={isSaving}
								onClick={() => setShow(false)}
							/>
							<Button
								text={trans("Submit")}
								type="primary"
								htmlType="submit"
								loading={isSaving}
							/>
						</div>
					</form>
				)}
			</Modal.Body>
		</Popup>
	);
}
